/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import React, { useCallback, useEffect, useState } from "react";
import { SpecialKey } from "@itwin/appui-abstract";
import {
  StagePanelLocation,
  StagePanelSection,
  UiItemsProvider,
  useActiveViewport,
  Widget,
  WidgetState
} from "@itwin/appui-react";
import { BackgroundMapType } from "@itwin/core-common";
import { IModelApp, Marker, NotifyMessageDetails, OutputMessagePriority } from "@itwin/core-frontend";
import { SvgHelpCircularHollow } from "@itwin/itwinui-icons-react";
import { Alert, Button, DefaultCell, IconButton, Input, Label, Table, Text, ToggleSwitch } from "@itwin/itwinui-react";
import { GlobalDisplayApi } from "./GlobalDisplayApi";
import "./GlobalDisplay.scss";
import { UseItwinClient } from "./hooks/useItwinClient";
import { UseSiiselClient } from "./hooks/useSiiselClient";
import { SmartDeviceMarker } from "./common/markers/SmartDeviceMarker";


const GlobalDisplayWidget = () => {
  const [markerSet, setMarkerSet] = useState<SmartDeviceMarker[]>([]);
  const [dataReady, setDataReady] = useState(false);
  const viewport = useActiveViewport();
  /** Place name to which to travel. */
  const [destination, setDestination] = useState<string>("");
  /** True for 3d terrain, false for a flat map. */
  const [terrain, setTerrain] = useState<boolean>(true);
  /** Display map labels with the map imagery. */
  const [mapLabels, setMapLabels] = useState<boolean>(true);
  /** Display 3d building meshes from Open Street Map Buildings. */
  const [buildings, setBuildings] = useState<boolean>(true);
  /** If buildings are displayed, also display their edges. */
  const [buildingEdges, setBuildingEdges] = useState<boolean>(false);

  useEffect(() => {
    if (viewport) {
      viewport.changeBackgroundMapProps({
        applyTerrain: terrain,
      });
      viewport.changeBackgroundMapProvider({ type: mapLabels ? BackgroundMapType.Hybrid : BackgroundMapType.Aerial });
    }
  }, [viewport, terrain, mapLabels]);

  useEffect(() => {
    if (viewport) {
      viewport.displayStyle.setOSMBuildingDisplay({ onOff: buildings });
    }
  }, [viewport, buildings]);

  useEffect(() => {
    if (viewport) {
      viewport.viewFlags = viewport.viewFlags.with("visibleEdges", buildingEdges);
    }
  }, [viewport, buildingEdges]);

  const _travelToDestination = async () => {
    if (!viewport)
      return;

    const locationFound = await GlobalDisplayApi.travelTo(viewport, destination);
    if (!locationFound) {
      const message = `Sorry, "${destination}" isn't recognized as a location.`;
      IModelApp.notifications.outputMessage(new NotifyMessageDetails(OutputMessagePriority.Warning, message));
    }
  };

  const _onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === SpecialKey.Enter || e.key === SpecialKey.Return) {
      _travelToDestination()
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);
        });
    }
  };

  const infoLabel = (label: string, tooltip: string) => (
    <span className="toggle-label">
      <Text>{label}</Text>
      <IconButton size="large" styleType="borderless" title={tooltip}><SvgHelpCircularHollow /></IconButton>
    </span>
  );

  const collectSensorData = async () => {
    let newMarkerSet: SmartDeviceMarker[] = [];

    const itwinClient = UseItwinClient();
    const siiselClient = UseSiiselClient();
    const iModelData = await itwinClient.getSmartDeviceData()
    const siiselSettings = await siiselClient.getItwinPoints(iModelData.map(value => value.id))

    for (const currentItem of siiselSettings.itwinMeasurementOutputs) {
      const siiselData = await siiselClient.fetchDataFromPath(currentItem.urlPath)
      const currentImodelItem = iModelData.find(x => x.id === currentItem.pointDwgId);
      newMarkerSet.push(new SmartDeviceMarker(
        { x: currentImodelItem.origin.x, y: currentImodelItem.origin.y, z: currentImodelItem.origin.z },
        { x: 50, y: 50 },
        currentImodelItem.smartDeviceId,
        currentImodelItem.smartDeviceType,
        siiselData,
        currentImodelItem.id
      ));
    }
    

   setMarkerSet(newMarkerSet)
   setDataReady(true)
  };

  useEffect(() => {
    collectSensorData();
  }, []);

  /* TABLE */

  const generateItem : any = useCallback((maker : SmartDeviceMarker) => {
      return {
      name: `${maker.label}`,
      warningState: 'NE',
    };
  }, []);


  const data = React.useMemo(
    () =>
      markerSet
        .map((maker, index) => generateItem(maker)),
    [markerSet],
  );

  const columns = React.useMemo(
    () => [
      {
        id: 'name',
        Header: 'Název',
        accessor: 'name',
        width: '40%',
      },
      {
        id: 'warningState',
        Header: 'Varovný stav',
        accessor: 'warningState',
        Cell: (props : any) => {
          return <>{props.value}</>;
        },
      },
    ],
    [],
  );

  const rowProps = React.useCallback((row) => {
    return {
      status: row.original.status,
    };
  }, []);

 
  return (
    <div className="sample-container">
      <div className="sample-options">
        <div className="sample-options-toggles">
          <ToggleSwitch
            label={infoLabel("Terén ve 3D", "Zdroj dat: Cesium World Terrain")}
            checked={terrain}
            onChange={() => setTerrain(!terrain)}
          />
          <ToggleSwitch
            label={infoLabel("Popisky", "Zdroj dat: Bing Maps")}
            checked={mapLabels}
            onChange={() => setMapLabels(!mapLabels)}
          />
          <ToggleSwitch
            label={infoLabel("Budovy", "Zdroj dat: OpenStreetMaps")}
            checked={buildings}
            onChange={() => setBuildings(!buildings)}
          />
          <ToggleSwitch
            label={infoLabel("Hrany budov", "Zdroj dat: OpenStreetMaps")}
            checked={buildingEdges}
            onChange={() => setBuildingEdges(!buildingEdges)}
          />
        </div>
        {/*<div className="travel-destination">
          <Label htmlFor="destination">{infoLabel("Destination", "Type a place name and press enter to travel there")}</Label>
          <Input id="destination" size="small" className="travel-destination-input" onChange={(e) => setDestination(e.currentTarget.value)} onKeyPress={_onKeyPress} />
          <Button size="small" className="travel-destination-btn" styleType="cta" disabled={!destination.length} onClick={_travelToDestination} title={"Travel to the specified destination"}>Travel</Button>
        </div>*/}
      </div> 
      {(markerSet.length != 0) ? (

        <div className='itwin-data-table'>
          <Table
            columns={columns}
            emptyTableContent='No data.'
            data={data}
            rowProps={rowProps}
            density='condensed'
          />
        </div>

      
      ) : (
      <Alert type="warning" className="instructions">
        Data nejsou k dispozici
      </Alert>)}
      
    </div>
  );

};

export class GlobalDisplayWidgetProvider implements UiItemsProvider {
  public readonly id: string = "GlobalDisplayWidgetProvider";

  public provideWidgets(_stageId: string, _stageUsage: string, location: StagePanelLocation, _section?: StagePanelSection): ReadonlyArray<Widget> {
    const widgets: Widget[] = [];
    if (location === StagePanelLocation.Bottom) {
      widgets.push(
        {
          id: "GlobalDisplayWidget",
          label: "Nastavení a připojená čidla",
          defaultState: WidgetState.Open,
          content: <GlobalDisplayWidget />,
        }
      );
    }
    return widgets;
  }
}
